import { type ComponentProps, type ReactElement, type ReactNode } from 'react';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Panel } from '@/ui/composites/Panel';
import { Box, useTheme } from '@/ui/primitives';
import { useMediaQuery } from '@/ui/primitives/hooks';
import { TABLE_PANEL_HEADER_HEIGHT } from '../tableSystem/tableConstants';
import { TableDatePopover } from '../tableSystem/TableDatePopover';
import {
  buildToolbarIconButtonSx,
  buildToolbarIconCircleSx,
} from '../tableSystem/tableToolbar.helpers';
import {
  type TableDropdownOption,
  type TableStatusOption,
} from '../tableSystem/tableToolbar.types';
import { useTableToolbarState } from '../tableSystem/useTableToolbarState';
import {
  resolveMetricsPageToolbarHeaderSlotLayout,
  resolveMetricsPageToolbarLayout,
} from './metricsPageToolbarLayout';
import { MetricsToolbarDateControls } from './MetricsToolbarDateControls';
import { MetricsToolbarInlineCluster } from './MetricsToolbarInlineCluster';
import { MetricsToolbarSearchControl } from './MetricsToolbarSearchControl';
import type { MetricsToolbarTagOption } from './metricsToolbarTagOptions';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { Dayjs } from 'dayjs';

const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: 'subtitle1.fontSize',
      lineHeight: 1.4,
    },
  },
} as const;

const metricsToolbarColumnVisibilityModel = {} satisfies GridColumnVisibilityModel;
const metricsToolbarColumnOptions: Array<{ field: string; mandatory?: boolean }> = [];
const metricsToolbarSortKey = 'default' as const;

export type MetricsPageToolbarProps<TStatus extends string> = {
  title: string;
  headerRightSlot?: ReactNode;
  allowWrappedHeader?: boolean;
  showToolbarControls?: boolean;
  showInlineFilters?: boolean;
  showDateControls?: boolean;
  showSearch?: boolean;
  headerHeight?: number;
  headerTitlePaddingLeft?: string | number;
  headerControlsPaddingRight?: string | number;
  searchAriaLabel: string;
  searchValue?: string;
  onDebouncedSearchChange: (value: string) => void;
  filterStatuses: TStatus[];
  statusOptions: Array<TableStatusOption<TStatus>>;
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterEntities?: string[];
  onFilterEntitiesChange?: (next: string[]) => void;
  entityOptions?: TableDropdownOption[];
  entityLabel?: string;
  filterItems?: string[];
  onFilterItemsChange?: (next: string[]) => void;
  itemOptions?: TableDropdownOption[];
  itemLabel?: string;
  filterSuites?: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  suiteOptions?: TableDropdownOption[];
  suiteLabel?: string;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  filterProjects?: string[];
  onFilterProjectsChange?: (next: string[]) => void;
  projectOptions?: TableDropdownOption[];
  dateQuickFilter: DateQuickFilter;
  onDateQuickFilterChange: (next: DateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  latestQueryStartDay?: string | null;
  latestQueryEndDay?: string | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
  availableTags: MetricsToolbarTagOption[];
  includeLatestQuickFilter?: boolean;
  statusFilterLabel?: string;
};

export const MetricsPageToolbar = <TStatus extends string>({
  title,
  headerRightSlot,
  allowWrappedHeader = false,
  showToolbarControls = true,
  showInlineFilters = false,
  showDateControls = true,
  showSearch = true,
  headerHeight = TABLE_PANEL_HEADER_HEIGHT,
  headerTitlePaddingLeft = s('sm'),
  headerControlsPaddingRight = s('sm'),
  searchAriaLabel,
  searchValue: externalSearchValue,
  onDebouncedSearchChange,
  filterStatuses,
  statusOptions,
  onFilterStatusesChange,
  filterEntities = [],
  onFilterEntitiesChange,
  entityOptions,
  entityLabel,
  filterItems = [],
  onFilterItemsChange,
  itemOptions,
  itemLabel,
  filterSuites = [],
  onFilterSuitesChange,
  suiteOptions,
  suiteLabel,
  filterTags,
  onFilterTagsChange,
  filterProjects,
  onFilterProjectsChange,
  projectOptions,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  latestQueryStartDay = null,
  latestQueryEndDay = null,
  onFromDateChange,
  onToDateChange,
  availableTags,
  includeLatestQuickFilter = false,
  statusFilterLabel = 'Status',
}: MetricsPageToolbarProps<TStatus>): ReactElement => {
  const theme = useTheme();
  const resolvedFilterStatuses = Array.isArray(filterStatuses) ? filterStatuses : [];
  const resolvedStatusOptions = Array.isArray(statusOptions) ? statusOptions : [];
  const resolvedFilterTags = Array.isArray(filterTags) ? filterTags : [];
  const resolvedFilterProjects = Array.isArray(filterProjects) ? filterProjects : undefined;
  const resolvedProjectOptions = Array.isArray(projectOptions) ? projectOptions : undefined;
  const resolvedAvailableTags = Array.isArray(availableTags) ? availableTags : [];
  const showSuiteFilter = filterSuites.length > 0 || (suiteOptions?.length ?? 0) > 0;
  const showEntityFilter = filterEntities.length > 0 || (entityOptions?.length ?? 0) > 0;
  const showItemFilter = filterItems.length > 0 || (itemOptions?.length ?? 0) > 0;
  const inlineFilterControlCount =
    (showSuiteFilter ? 1 : 0) +
    (showEntityFilter ? 1 : 0) +
    (showItemFilter ? 1 : 0) +
    (resolvedStatusOptions.length > 0 ? 1 : 0) +
    1 +
    ((resolvedProjectOptions?.length ?? 0) > 0 ? 1 : 0) +
    (showSearch ? 1 : 0);
  const collapseAtXl = useMediaQuery(theme.breakpoints.down('xl'));
  const collapseAtLg = useMediaQuery(theme.breakpoints.down('lg'));
  const collapseAtMd = useMediaQuery(theme.breakpoints.down('md'));
  const hideDateRangeLabel = useMediaQuery(theme.breakpoints.down('sm'));
  const { isCompactHeaderLayout, useWrappedHeaderLayout, useCollapsedToolbarControls } =
    resolveMetricsPageToolbarLayout({
      allowWrappedHeader,
      showToolbarControls,
      hasHeaderRightSlot: headerRightSlot !== undefined,
      showInlineFilters,
      inlineFilterControlCount,
      collapseAtXl,
      collapseAtLg,
      collapseAtMd,
    });
  const headerSlotLayout = resolveMetricsPageToolbarHeaderSlotLayout({
    hasHeaderRightSlot: headerRightSlot !== undefined,
    isCompactHeaderLayout,
    showToolbarControls,
    useWrappedHeaderLayout,
  });
  const toolbarControlSize = 32;
  const toolbarExpandedSearchWidth = 180;
  const toolbarSearchWidthPx = `${toolbarExpandedSearchWidth}px`;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const toolbarIconCircleSx = buildToolbarIconCircleSx;
  const {
    popoverState,
    searchValue,
    searchInputRef,
    hasActiveFilters,
    hasActiveToolbarState,
    dateRangeLabel,
    displayedFromDate,
    displayedToDate,
    setSearchValue,
    handleSearchBlur,
    handleSearchKeyDown,
    openPopover,
    closePopover,
    preserveWindowScroll,
    resetToolbarFilters,
  } = useTableToolbarState<typeof metricsToolbarSortKey, TStatus>({
    searchValue: externalSearchValue,
    onDebouncedSearchChange,
    columnVisibilityModel: metricsToolbarColumnVisibilityModel,
    defaultColumnVisibilityModel: metricsToolbarColumnVisibilityModel,
    columnOptions: metricsToolbarColumnOptions,
    onColumnVisibilityModelChange: () => undefined,
    sortKey: metricsToolbarSortKey,
    sortDirection: 'desc',
    defaultSortKey: metricsToolbarSortKey,
    defaultSortDirection: 'desc',
    onSortChange: () => undefined,
    filterStatuses: resolvedFilterStatuses,
    onFilterStatusesChange,
    filterEntities,
    onFilterEntitiesChange,
    filterItems,
    onFilterItemsChange,
    filterSuites,
    onFilterSuitesChange,
    filterTags: resolvedFilterTags,
    onFilterTagsChange,
    dateQuickFilter,
    onDateQuickFilterChange,
    fromDate,
    toDate,
    latestQueryStartDay,
    latestQueryEndDay,
    onFromDateChange,
    onToDateChange,
  });
  const toolbarIconTooltipDelayMs = 250;
  const hasActiveInlineFilterState = hasActiveFilters || searchValue.trim().length > 0;
  const hasCustomDateRange = fromDate !== null || toDate !== null;
  const controlsOffsetRight =
    typeof headerControlsPaddingRight === 'number'
      ? `${headerControlsPaddingRight * -1}px`
      : `calc(${headerControlsPaddingRight} * -1)`;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={useWrappedHeaderLayout ? 'flex-start' : 'center'}
        wrap={useWrappedHeaderLayout ? 'wrap' : 'nowrap'}
        gap="md"
        sx={{
          width: '100%',
          backgroundColor: 'background.paper',
          minHeight: `${headerHeight}px`,
          height: useWrappedHeaderLayout ? 'auto' : `${headerHeight}px`,
          rowGap: useWrappedHeaderLayout ? s('xs') : s('none'),
        }}
      >
        <Box
          sx={{
            minHeight: `${toolbarControlSize}px`,
            display: 'flex',
            alignItems: 'center',
            pl: headerTitlePaddingLeft,
            minWidth: 0,
            width: 'auto',
            flex: useWrappedHeaderLayout
              ? '0 0 auto'
              : isCompactHeaderLayout
                ? '0 0 auto'
                : '1 1 auto',
          }}
        >
          <MetricsToolbarInlineCluster
            title={title}
            showInlineFilters={showInlineFilters}
            useCollapsedToolbarControls={useCollapsedToolbarControls}
            toolbarControlSize={toolbarControlSize}
            hasActiveInlineFilterState={hasActiveInlineFilterState}
            useWrappedHeaderLayout={useWrappedHeaderLayout}
            popoverState={popoverState}
            openPopover={openPopover}
            closePopover={closePopover}
            searchValue={searchValue}
            searchAriaLabel={searchAriaLabel}
            setSearchValue={setSearchValue}
            searchInputRef={searchInputRef}
            handleSearchBlur={handleSearchBlur}
            handleSearchKeyDown={handleSearchKeyDown}
            hasActiveFilters={hasActiveFilters}
            statusOptions={resolvedStatusOptions}
            statusFilterLabel={statusFilterLabel}
            filterStatuses={resolvedFilterStatuses}
            onFilterStatusesChange={onFilterStatusesChange}
            filterEntities={filterEntities}
            onFilterEntitiesChange={onFilterEntitiesChange}
            entityOptions={entityOptions}
            entityLabel={entityLabel}
            filterItems={filterItems}
            onFilterItemsChange={onFilterItemsChange}
            itemOptions={itemOptions}
            itemLabel={itemLabel}
            filterSuites={filterSuites}
            onFilterSuitesChange={onFilterSuitesChange}
            suiteOptions={suiteOptions}
            suiteLabel={suiteLabel}
            filterTags={resolvedFilterTags}
            onFilterTagsChange={onFilterTagsChange}
            availableTags={resolvedAvailableTags}
            filterProjects={resolvedFilterProjects}
            onFilterProjectsChange={onFilterProjectsChange}
            projectOptions={resolvedProjectOptions}
            preserveWindowScroll={preserveWindowScroll}
            toolbarInactiveIconColor={toolbarInactiveIconColor}
            tooltipSlotProps={CLONE_TOOLTIP_SLOT_PROPS}
            showSearch={showSearch}
            toolbarExpandedSearchWidth={toolbarExpandedSearchWidth}
            toolbarSearchWidthPx={toolbarSearchWidthPx}
          />
        </Box>
        {showToolbarControls ? (
          <Stack
            direction="row"
            alignItems="center"
            gap="sm"
            sx={{
              minWidth: 0,
              height: `${headerHeight}px`,
              minHeight: `${headerHeight}px`,
              boxSizing: 'border-box',
              justifyContent: 'center',
              pr: headerControlsPaddingRight,
            }}
          >
            {!showInlineFilters && showSearch ? (
              <MetricsToolbarSearchControl
                toolbarExpandedSearchWidth={toolbarExpandedSearchWidth}
                toolbarControlSize={toolbarControlSize}
                toolbarSearchWidthPx={toolbarSearchWidthPx}
                searchValue={searchValue}
                searchAriaLabel={searchAriaLabel}
                setSearchValue={setSearchValue}
                searchInputRef={searchInputRef}
                handleSearchBlur={handleSearchBlur}
                handleSearchKeyDown={handleSearchKeyDown}
              />
            ) : null}
            {showDateControls ? (
              <MetricsToolbarDateControls
                cloneTooltipSlotProps={CLONE_TOOLTIP_SLOT_PROPS}
                dateQuickFilter={dateQuickFilter}
                dateRangeLabel={dateRangeLabel ?? ''}
                hasActiveToolbarState={hasActiveToolbarState}
                hasCustomDateRange={hasCustomDateRange}
                hideDateRangeLabel={hideDateRangeLabel}
                includeLatestQuickFilter={includeLatestQuickFilter}
                onDateQuickFilterChange={onDateQuickFilterChange}
                onOpenCustomDateFilters={openPopover('date')}
                onResetToolbarFilters={resetToolbarFilters}
                preserveWindowScroll={preserveWindowScroll}
                toolbarControlSize={toolbarControlSize}
                toolbarIconButtonSx={toolbarIconButtonSx}
                toolbarIconCircleSx={toolbarIconCircleSx}
                toolbarIconTooltipDelayMs={toolbarIconTooltipDelayMs}
              />
            ) : null}
          </Stack>
        ) : headerSlotLayout.shouldRenderHeaderRightSlot ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: headerSlotLayout.justifyContent,
              minWidth: 0,
              width: headerSlotLayout.width,
              flex: headerSlotLayout.flex,
              minHeight: `${headerHeight}px`,
              height: useWrappedHeaderLayout ? 'auto' : `${headerHeight}px`,
              mr: headerSlotLayout.useControlsOffsetMargin ? controlsOffsetRight : 0,
              pl: headerSlotLayout.useHeaderTitlePaddingLeft ? headerTitlePaddingLeft : s('none'),
            }}
          >
            {headerRightSlot}
          </Box>
        ) : null}
      </Stack>
      <TableDatePopover
        open={popoverState.type === 'date'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        dateQuickFilter={dateQuickFilter}
        fromDate={fromDate}
        toDate={toDate}
        previewFromDate={displayedFromDate}
        previewToDate={displayedToDate}
        onFromDateChange={(next) => {
          preserveWindowScroll(() => {
            onFromDateChange(next);
          });
        }}
        onToDateChange={(next) => {
          preserveWindowScroll(() => {
            onToDateChange(next);
          });
        }}
      />
    </>
  );
};

export type MetricsPageToolbarPanelProps<TStatus extends string> =
  MetricsPageToolbarProps<TStatus> & {
    width?: number;
    panelPadding?: ComponentProps<typeof Panel>['padding'];
    panelPaddingX?: string | number;
  };

export const MetricsPageToolbarPanel = <TStatus extends string>({
  width,
  panelPadding = 'xl',
  panelPaddingX = s('none'),
  ...toolbarProps
}: MetricsPageToolbarPanelProps<TStatus>): ReactElement => (
  <Panel
    padding="none"
    gap="none"
    contentGap="none"
    headerGap="none"
    headerSlot={
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: r('card'),
          overflow: 'hidden',
          minHeight: `${toolbarProps.headerHeight ?? TABLE_PANEL_HEADER_HEIGHT}px`,
        }}
      >
        <MetricsPageToolbar {...toolbarProps} />
      </Box>
    }
    sx={[
      {
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        px: panelPaddingX,
        pt: panelPadding,
        pb: panelPadding,
      },
      width
        ? {
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
          }
        : null,
    ]}
  >
    {null}
  </Panel>
);
